"use client";

import type React from "react";
import { useRouter } from "next/navigation";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import { useIntentRedirectModal } from "@hooks/useIntentRedirectModal";
import type { PageType } from "@constants/pageTypes";
import type { BuyingIntentData } from "@services/globalIntentService";
import { updateAppBar } from "@hooks/useWebviewCallback";
import { COMPLETE_TRANSPARENT_APPBAR_CONFIG } from "@utils/appbarConfig";

interface VariantCardAppQuoteButtonProps {
  className?: string;
  href: string;
  pageType?: PageType;
  eventProperties: Record<string, unknown>;
  buyingIntent?: BuyingIntentData | null;
  cityName: string;
  brandName?: string;
  modelName?: string;
  variantName: string;
  children: React.ReactNode;
}

const VariantCardAppQuoteButton = ({
  className,
  href,
  pageType,
  eventProperties,
  buyingIntent,
  cityName,
  brandName,
  modelName,
  variantName,
  children,
}: VariantCardAppQuoteButtonProps) => {
  const router = useRouter();
  const { showLoader } = useGlobalLoader();
  const { intentModal, openIntentOrRedirect } = useIntentRedirectModal({
    buyingIntent,
    cityName,
    brandName,
    modelName,
    variantName,
    pageType,
    loginType: "Get quote",
    continueRedirect: (redirectUrl) => {
      showLoader("Loading...");
      router.push(redirectUrl);
    },
    disableClose: true,
  });

  const handleClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    updateAppBar(COMPLETE_TRANSPARENT_APPBAR_CONFIG);
    showLoader();
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType,
      additionalProps: eventProperties,
    });
    openIntentOrRedirect({ redirectUrl: href });
  };

  return (
    <>
      <a className={className} href={href} onClick={handleClick}>
        {children}
      </a>
      {intentModal}
    </>
  );
};

export default VariantCardAppQuoteButton;
